import { useSyncExternalStore } from "react";

import api from "../services/api";
import { abrirAudio } from "./audio";

// Estado das ligações de WhatsApp (módulo "calls"), compartilhado entre o
// widget flutuante, o botão do ticket e a página de histórico.
//
// O backend manda cada mudança pelo socket ("ligacao"); aqui só se guarda a
// última versão de cada ligação e qual delas tem o áudio neste navegador.

let state = {
	disponivel: false, // dá para ligar: motor de pé + ao menos um aparelho
	motorRodando: false, // módulo instalado, rodando e contratado pela empresa
	conexoes: [], // conexões com aparelho de ligações
	chamadas: {}, // id -> ligação (só as não encerradas + recém-encerradas)
	minha: null, // { id, mudo, espera, audio: "abrindo"|"conectado"|"fechado"|"erro" }
	aberto: false, // painel do telefone aberto (false = minimizado)
	numero: "", // o que está digitado no teclado
	userId: null,
};

const subs = new Set();
const emit = () => {
	state = { ...state };
	subs.forEach((fn) => fn());
};
const set = (patch) => {
	Object.assign(state, patch);
	emit();
};

const subscribe = (fn) => {
	subs.add(fn);
	return () => subs.delete(fn);
};
const getSnapshot = () => state;

export const useLigacoes = () => useSyncExternalStore(subscribe, getSnapshot);

let audio = null;

// Microfone escolhido na página Ligações › Este computador. É do computador,
// não da conta: fica no navegador.
export const CHAVE_MICROFONE = "ligacoes_microfone";
export const microfoneEscolhido = () => {
	try {
		return localStorage.getItem(CHAVE_MICROFONE) || null;
	} catch {
		return null;
	}
};

const guardar = (ligacao) => {
	if (!ligacao?.id) return;
	const chamadas = { ...state.chamadas, [ligacao.id]: ligacao };
	set({ chamadas });
	// Encerrada some do widget depois de alguns segundos (dá tempo de ver
	// "não atendida" / duração).
	if (ligacao.endedAt) {
		setTimeout(() => {
			const atual = state.chamadas[ligacao.id];
			if (atual?.endedAt) {
				const resto = { ...state.chamadas };
				delete resto[ligacao.id];
				set({ chamadas: resto });
			}
		}, 6000);
	}
};

const fecharAudio = () => {
	if (audio) {
		audio.encerrar();
		audio = null;
	}
};

const ligarAudio = async (id) => {
	fecharAudio();
	set({ minha: { id, mudo: false, espera: false, audio: "abrindo" } });
	try {
		audio = await abrirAudio(id, {
			microfoneId: microfoneEscolhido(),
			aoMudarEstado: (e) => {
				if (state.minha?.id === id) set({ minha: { ...state.minha, audio: e } });
			},
		});
	} catch (err) {
		set({ minha: { ...state.minha, audio: "erro", erro: err.message } });
		throw err;
	}
};

export const nivelRemoto = () => (audio ? audio.nivelRemoto() : 0);
export const nivelLocal = () => (audio ? audio.nivelLocal() : 0);
// "opus" (comprimido, ~24 kbps) ou "pcm16" (reserva). Nulo antes de conectar.
export const modoDoAudio = () => (audio ? audio.modo() : null);

// ------------------------------------------------------------ inicialização

export const configurar = async ({ userId, temModulo }) => {
	set({ userId });
	if (!temModulo) {
		set({ disponivel: false, motorRodando: false, conexoes: [] });
		return;
	}
	try {
		const { data: status } = await api.get("/installed-modules/status");
		const rodando = status.some((m) => m.key === "calls" && m.running);
		if (!rodando) {
			set({ disponivel: false, motorRodando: false, conexoes: [] });
			return;
		}
		const [{ data: conexoes }, { data: ativas }] = await Promise.all([
			api.get("/calls/connections"),
			api.get("/calls/active"),
		]);
		const chamadas = {};
		ativas.forEach((l) => {
			chamadas[l.id] = l;
		});
		set({
			disponivel: conexoes.engineRunning && conexoes.connections.length > 0,
			motorRodando: conexoes.engineRunning,
			conexoes: conexoes.connections,
			chamadas,
		});
	} catch {
		set({ disponivel: false, motorRodando: false });
	}
};

export const recarregarConexoes = async () => {
	try {
		const { data } = await api.get("/calls/connections");
		set({
			disponivel: data.engineRunning && data.connections.length > 0,
			motorRodando: data.engineRunning,
			conexoes: data.connections,
		});
	} catch {
		// mantém o que tinha
	}
};

// Evento do socket "ligacao".
export const aoEvento = ({ action, call, ringUserIds }) => {
	// Quem deve ouvir o toque vem só no evento de chegada; guarda junto da
	// ligação para as atualizações seguintes não apagarem.
	const anterior = state.chamadas[call.id];
	call.ringUserIds = ringUserIds !== undefined ? ringUserIds : anterior?.ringUserIds ?? null;
	guardar(call);
	// Ligação chegando para mim: o telefone abre sozinho, mesmo minimizado.
	if (action === "incoming" && (!call.ringUserIds || call.ringUserIds.includes(state.userId))) {
		set({ aberto: true });
	}
	const minhaId = state.minha?.id;
	if (minhaId === call.id && call.endedAt) {
		fecharAudio();
		set({ minha: null });
	}
	return action;
};

// ------------------------------------------------------------ ações

export const ligar = async ({ ticketId, number, whatsappId }) => {
	if (state.minha) throw new Error("Encerre a ligação atual antes de fazer outra.");
	const { data } = await api.post("/calls", { ticketId, number, whatsappId });
	guardar(data);
	set({ numero: "", aberto: true });
	await ligarAudio(data.id);
	return data;
};

export const atender = async (id) => {
	if (state.minha && state.minha.id !== id) {
		// Atender em cima de outra: põe a atual em espera antes.
		await alternarEspera(true).catch(() => {});
	}
	const { data } = await api.post(`/calls/${id}/accept`);
	guardar(data);
	await ligarAudio(id);
};

export const recusar = async (id) => {
	await api.post(`/calls/${id}/reject`);
};

export const desligar = async (id = state.minha?.id) => {
	if (!id) return;
	try {
		await api.post(`/calls/${id}/hangup`);
	} finally {
		if (state.minha?.id === id) {
			fecharAudio();
			set({ minha: null });
		}
	}
};

export const alternarMudo = () => {
	if (!state.minha) return;
	const mudo = !state.minha.mudo;
	audio?.definirMudo(mudo);
	set({ minha: { ...state.minha, mudo } });
};

export const alternarEspera = async (forcar) => {
	if (!state.minha) return;
	const espera = forcar ?? !state.minha.espera;
	await api.post(`/calls/${state.minha.id}/${espera ? "hold" : "resume"}`);
	audio?.definirMudo(espera || state.minha.mudo);
	set({ minha: { ...state.minha, espera } });
};

// Abre o telefone; com número, já deixa digitado para a pessoa conferir.
export const abrirTelefone = (numero) =>
	set({ aberto: true, ...(numero ? { numero: String(numero).replace(/\D/g, "") } : {}) });
export const minimizarTelefone = () => set({ aberto: false });
export const definirNumero = (numero) => set({ numero });

export const encerrarTudo = () => {
	fecharAudio();
	set({ minha: null, chamadas: {} });
};
